<template>
  <div class="tab-content-inner">
    <div class="section-label">Conexão com o provedor</div>

    <!--
      O estado real da integração, e não um formulário.

      Provedor, token, modelos e limites são configurados uma única vez, no
      módulo de Integrações. Esta aba existe para que quem chega por aqui veja
      em que pé está a conexão e saiba para onde ir — antes, os mesmos campos
      existiam nos dois lugares, e alterá-los aqui não produzia efeito nenhum.
    -->
    <div v-if="carregando" class="estado-conexao estado-conexao--neutro">
      Consultando a integração...
    </div>

    <div v-else class="estado-conexao" :class="'estado-conexao--' + estado.aparencia">
      <div class="estado-conexao__texto">
        <div class="estado-conexao__titulo">
          {{ estado.titulo }}
          <ChipStatusIntegracao
            v-if="integracao"
            :ativo="!!integracao.ativo"
            :status-conexao="integracao.status_conexao"
          />
        </div>
        <div class="estado-conexao__descricao">{{ estado.descricao }}</div>

        <dl v-if="integracao" class="resumo">
          <div class="resumo__item">
            <dt>Provedor</dt>
            <dd>{{ integracao.provedor_nome || integracao.provedor || "—" }}</dd>
          </div>
          <div class="resumo__item">
            <dt>Modelo principal</dt>
            <dd>{{ valorDaIntegracao("model") }}</dd>
          </div>
          <div class="resumo__item">
            <dt>Modelo para áudio</dt>
            <dd>{{ valorDaIntegracao("model_para_audio") }}</dd>
          </div>
          <div class="resumo__item">
            <dt>Itens para análise</dt>
            <dd>{{ valorDaIntegracao("itens_para_analise") }}</dd>
          </div>
          <div class="resumo__item">
            <dt>Máximo de tokens de entrada</dt>
            <dd>{{ valorDaIntegracao("max_tokens_entrada") }}</dd>
          </div>
        </dl>
      </div>

      <CButton
        v-if="erroConsulta"
        color="primary"
        class="estado-conexao__acao"
        @click="carregarIntegracao"
      >
        Tentar novamente
      </CButton>

      <CButton
        v-else-if="!semAcessoAIntegracoes"
        color="primary"
        class="estado-conexao__acao"
        @click="abrirIntegracao"
      >
        {{ integracao ? "Abrir a integração" : "Habilitar a integração" }}
      </CButton>
    </div>

    <!--
      A segunda metade do "está pronto para usar".

      Credencial válida no provedor não basta: a sugestão de pedidos compara o
      texto do cliente com o catálogo indexado na base vetorial, que é um
      serviço à parte. Sem ele, a sugestão volta vazia e a tela não dizia por
      quê. Não há botão de ativar de propósito — a ativação é da Versotech, e
      um botão prometeria o que o cliente não tem como fazer sozinho.
    -->
    <div class="section-label">Busca por produtos (sugestão de pedidos)</div>

    <div v-if="carregandoBase" class="estado-conexao estado-conexao--neutro">
      Consultando os serviços da busca...
    </div>

    <!--
      Um bloco por serviço, e não um indicador só: eles quebram separados e
      pedem ações diferentes — catálogo sem índice espera a carga, leitura do
      pedido fora do ar espera o serviço subir. Com um indicador só, a tela
      chegou a dizer "catálogo indexado e pronto" com 14 mil produtos enquanto
      nenhuma sugestão saía.
    -->
    <div v-if="erroBaseVetorial && !carregandoBase" class="estado-conexao estado-conexao--falha">
      <div class="estado-conexao__texto">
        <div class="estado-conexao__titulo">Não foi possível consultar os serviços da busca</div>
        <div class="estado-conexao__descricao">
          A consulta falhou — não é o mesmo que a busca estar fora do ar.
        </div>
      </div>
      <CButton color="primary" class="estado-conexao__acao" @click="carregarBaseVetorial">
        Tentar novamente
      </CButton>
    </div>

    <div
      v-for="servico in servicosDaBusca"
      :key="servico.chave"
      class="estado-conexao estado-conexao--servico"
      :class="'estado-conexao--' + aparenciaDoServico(servico)"
    >
      <div class="estado-conexao__texto">
        <div class="estado-conexao__rotulo">{{ servico.rotulo }}</div>
        <div class="estado-conexao__titulo">{{ servico.titulo }}</div>
        <div class="estado-conexao__descricao">{{ servico.descricao }}</div>
      </div>
    </div>

    <!-- Apoio -->
    <div class="section-label">Como tirar proveito</div>

    <div class="apoio">
      <p class="apoio__texto">
        A documentação cobre o uso do recurso ponta a ponta. A calibragem para a sua
        operação — dicionário de termos, prompts e critérios de avaliação — é feita em
        horas de treinamento com a Versotech, contratadas à parte.
      </p>
      <a
        class="apoio__link"
        :href="LINK_DOCUMENTACAO"
        target="_blank"
        rel="noopener noreferrer"
      >
        Abrir a documentação <fa-icon icon="angle-right" />
      </a>
    </div>

    <!-- Callcenter -->
    <div class="section-label">Callcenter</div>

    <ToggleSwitch
      label="Modo Aprendizado"
      ajuda="Permite que operadores corrijam termos de busca da IA diretamente na tela de sugestões"
      :value="!!parametro.modo_aprendizado"
      @input="update({ modo_aprendizado: $event })"
    />
  </div>
</template>

<script>
import { get } from "@/helpers/apiRequest";
import ChipStatusIntegracao from "../Integracoes/components/ChipStatusIntegracao.vue";
import ToggleSwitch from "@/components/ToggleSwitch.vue";

const SLUG = "ia-generativa";
const ORIGEM = "configuracao-ia";

/*
 * Mora aqui, e não no arquivo de idioma do backend, porque é o único endereço
 * que a tela usa por conta própria — o resto dos textos desta aba descreve
 * estado, e estado vem do servidor.
 */
const LINK_DOCUMENTACAO =
  "https://docs.versotech.com.br/integracoes-externas/"
  + "pedidos-com-ia-transforme-mensagense-midias-em-pedidos-estruturados-automaticamente";

/* Cada estado da base pede uma cor diferente porque pede uma ação diferente:
   acionar o suporte, esperar a carga, ou nada. */
const APARENCIA_DA_BASE = {
  indisponivel: "falha",
  sem_indice: "ausente",
  pronta: "ok",
  leitura_fora: "falha",
  leitura_no_ar: "ok",
};

export default {
  name: "tab-conexao",
  components: { ChipStatusIntegracao, ToggleSwitch },
  props: {
    parametro: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      LINK_DOCUMENTACAO,
      carregando: true,
      carregandoBase: true,
      baseVetorial: null,
      integracao: null,
      // Quem edita prompts não necessariamente administra integrações. Sem a
      // permissão, a consulta é recusada — e dizer "não foi habilitada" seria
      // um palpite, além de oferecer um botão que não abre nada.
      semAcessoAIntegracoes: false,
      // Consulta que falhou por outro motivo que não a permissão. Sem separar
      // os dois casos, a tela dizia "ainda não foi habilitada" para quem tem a
      // integração no ar e oferecia instalá-la de novo.
      erroConsulta: false,
      erroBaseVetorial: false,
    };
  },
  computed: {

    /**
     * Os serviços da busca, um por bloco. A lista vem pronta da API; a
     * reserva mantém a tela de pé se a resposta for de uma versão anterior,
     * que devolvia só o estado do catálogo.
     */
    servicosDaBusca() {
      if (!this.baseVetorial) {
        return [];
      }

      return this.baseVetorial.servicos || [this.baseVetorial];
    },

    /*
     * Três situações, e cada uma leva a um lugar diferente: nunca instalada,
     * instalada e desligada, no ar. Dizer só "configure em Integrações" nos
     * três casos faria quem já instalou procurar o que já existe.
     */
    estado() {
      if (this.erroConsulta) {
        return {
          aparencia: "falha",
          titulo: "Não foi possível consultar a integração",
          descricao:
            "A consulta ao módulo de Integrações falhou. O estado abaixo pode estar "
            + "desatualizado — os prompts e critérios desta tela continuam editáveis.",
        };
      }

      if (this.semAcessoAIntegracoes) {
        return {
          aparencia: "neutro",
          titulo: "A conexão é administrada em Integrações",
          descricao:
            "Provedor, token da API, modelos e limites de consumo ficam no módulo de Integrações, "
            + "a que este usuário não tem acesso. Peça a quem administra o painel para conferir a "
            + "integração de IA Generativa. Os prompts e critérios desta tela continuam editáveis.",
        };
      }

      if (!this.integracao) {
        return {
          aparencia: "ausente",
          titulo: "A IA ainda não foi habilitada",
          descricao:
            "Provedor, token da API, modelos e limites de consumo são definidos no módulo de "
            + "Integrações. Enquanto a integração não existir, os prompts e critérios configurados "
            + "aqui ficam guardados, mas nada é enviado ao provedor.",
        };
      }

      if (!this.integracao.ativo) {
        return {
          aparencia: "inativa",
          titulo: "A integração existe, mas está desativada",
          descricao:
            "Nenhuma chamada é feita ao provedor enquanto ela estiver assim. Reative-a na própria "
            + "integração para voltar a usar os recursos de IA.",
        };
      }

      if (this.integracao.status_conexao === "FALHA") {
        return {
          aparencia: "falha",
          titulo: "A última tentativa de conexão falhou",
          descricao:
            this.integracao.ultimo_erro
            || "Confira o token da API e o provedor na integração e teste a conexão novamente.",
        };
      }

      return {
        aparencia: "ok",
        titulo: "A IA está habilitada",
        descricao:
          "O provedor, o token e os modelos abaixo vêm da integração. Para alterá-los, abra a "
          + "integração — esta tela cuida dos prompts, critérios e dicionário.",
      };
    },
  },
  methods: {
    aparenciaDoServico(servico) {
      return APARENCIA_DA_BASE[servico && servico.estado] || "neutro";
    },

    update(changes) {
      this.$emit("update", changes);
    },

    /*
     * Os valores chegam mascarados no que é secreto, e o token nem é exibido
     * aqui: quem precisa vê-lo está na integração, não nesta tela.
     */
    valorDaIntegracao(chave) {
      const valor = (this.integracao && this.integracao.valores) ? this.integracao.valores[chave] : null;

      return valor === null || valor === undefined || valor === "" ? "—" : valor;
    },

    /*
     * A origem viaja junto para que o botão de voltar da outra tela devolva o
     * usuário aqui, e não à lista de integrações, que não era onde ele estava.
     */
    abrirIntegracao() {
      const destino = this.integracao
        ? `/integracoes/${this.integracao.id}/configurar`
        : `/integracoes/instalar/${SLUG}`;

      this.$router.push({ path: destino, query: { origem: ORIGEM } });
    },

    carregarIntegracao() {
      this.carregando = true;

      this.erroConsulta = false;

      get("/admin/integracoes")
        .then((integracoes) => {
          this.integracao = (integracoes || []).find((item) => item.catalogo_slug === SLUG) || null;
          this.carregando = false;
        })
        .catch((erro) => {
          // Falhar aqui não pode esconder os prompts: a aba mostra o que sabe
          // e o resto da tela segue utilizável.
          this.integracao = null;
          this.semAcessoAIntegracoes = !!erro && erro.apiStatus === 403;
          this.erroConsulta = !this.semAcessoAIntegracoes;
          this.carregando = false;
        });
    },

    carregarBaseVetorial() {
      this.carregandoBase = true;

      this.erroBaseVetorial = false;

      get("/admin/configuracao-ia/base-vetorial")
        .then((resposta) => {
          this.baseVetorial = (resposta && resposta.data) || null;
          this.carregandoBase = false;
        })
        .catch(() => {
          // Não dá para afirmar que a base está fora quando quem falhou foi a
          // consulta: o bloco diz que não conseguiu perguntar, e oferece
          // tentar de novo em vez de sumir sem explicação.
          this.baseVetorial = null;
          this.erroBaseVetorial = true;
          this.carregandoBase = false;
        });
    },
  },
  mounted() {
    this.carregarIntegracao();
    this.carregarBaseVetorial();
  },
};
</script>

<style scoped>
.tab-content-inner {
  padding: 20px;
}

.section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #667085;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
  margin-top: 20px;
}

.section-label:first-child {
  margin-top: 0;
}

.estado-conexao {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 16px 18px;
  border: 1px solid #e4e7eb;
  border-left-width: 4px;
  border-radius: 10px;
  background: #fff;
}

.estado-conexao--neutro {
  border-left-color: #cfd4dc;
  color: #667085;
  font-size: 13px;
}

.estado-conexao--ok {
  border-left-color: #1c7a44;
  background: #f6fbf8;
}

.estado-conexao--inativa {
  border-left-color: #667085;
  background: #f8f9fa;
}

.estado-conexao--ausente {
  border-left-color: #2298a3;
  background: #edf8f9;
}

.estado-conexao--falha {
  border-left-color: #b42318;
  background: #fdf5f4;
}

.estado-conexao__acao {
  white-space: nowrap;
}

/* Um bloco embaixo do outro, com respiro entre eles. */
.estado-conexao--servico + .estado-conexao--servico {
  margin-top: 10px;
}

/*
 * O papel do serviço vem acima do estado: lendo a coluna de rótulos de cima a
 * baixo, sabe-se o que a busca precisa ter no ar, mesmo quando está tudo bem.
 */
.estado-conexao__rotulo {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #98a2b3;
  margin-bottom: 2px;
}

.estado-conexao__titulo {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #344054;
  margin-bottom: 4px;
}

.estado-conexao__descricao {
  font-size: 13px;
  color: #667085;
  line-height: 1.5;
  max-width: 720px;
}

.resumo {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 32px;
  margin: 14px 0 0;
}

.resumo__item {
  min-width: 150px;
}

.resumo dt {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #667085;
  font-weight: 600;
  margin-bottom: 2px;
}

.resumo dd {
  margin: 0;
  font-size: 13px;
  color: #344054;
}

.apoio {
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  padding: 14px 18px;
}

.apoio__texto {
  font-size: 13px;
  color: #667085;
  line-height: 1.55;
  max-width: 820px;
  margin-bottom: 10px;
}

.apoio__link {
  font-size: 13px;
  font-weight: 600;
  color: #2298a3;
}

</style>
